/**
 * The public proof page — the reference's `Screens.public` (`m09-public.js:40`) inside the public
 * shell from `kaynak/screens.html:196`.
 *
 * Nine states share one address and one layout: four live records (person badge, participation
 * certificate, company certificate, expired diploma), two waiting records that show no personal
 * data at all, a revoked record whose successor exists, that successor, and a code that resolves
 * to nothing. Which one a visitor gets is decided by the derived view state and the issuance
 * release — never by the mutable template — so an old record keeps saying what it said the day it
 * was issued (binding rules 3 and 4).
 *
 * Two rules from the contract are visible in the markup rather than only in the data:
 *
 *   - a waiting record renders no name, no claims and no artwork (rule 12). The pending branch
 *     below has no access to them, which is stronger than hiding them with CSS;
 *   - an ambiguous code is NOT «not found» and is never resolved by showing one of the matches
 *     (§13). It gets its own 409 answer and leaks neither the count nor any matching code.
 *
 * Every CTA comes from `resolveLinkedInCTA`; this screen makes no LinkedIn decision of its own,
 * which is what keeps rules 13–15 true on the one surface a visitor actually sees.
 */
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useV07Commands } from '../data/commands-context';
import { useDemoData, useSettings } from '../data/store';
import type { DemoCredential, DemoDatabase, DemoSettings } from '../data/types';
import { useGoToCredential } from '../lib/go-to-credential';
import { useInteractionRecorder, usePageViewed } from '../lib/interaction';
import {
  addToProfileLinkForCredential,
  credentialIssuingOrganization,
} from '../lib/linkedin-issuing-organization';
import { verifiedOrganizationIdForPage } from '../lib/linkedin-organization';
import {
  buildOgImage,
  credentialPngDataUrl,
  downloadDataUrl,
  openNewTab,
  printCredential,
} from '../lib/public-artifacts';
import {
  credPublicFields,
  credVisualClass,
  HELPER_I18N,
  type LinkedInCTA,
  linkedInAllText,
  linkedInFields,
  linkedInShareUrl,
  lookupByPublicCode,
  PUBLIC_I18N,
  type PublicLanguage,
  resolveLinkedInCTA,
  SHARE_I18N,
  type ShareStrings,
  suggestedShareText,
} from '../lib/public-page';
import {
  credCriteria,
  credDescription,
  credentialViewState,
  credIssuerStatement,
  credTitle,
  displayName,
  fmtDate,
  publicUrl,
} from '../lib/reference';
import { credentialArtwork, credTerms, sealedInLanguage, servedArtifact } from '../lib/render';
import { memberPostShareImage, serverShareCard, useShareCardImage } from '../lib/share-card';
import { buildCommentary } from '../lib/share-commentary';
import { rememberedShareText, rememberShareText } from '../lib/share-text';
import { PublicShell } from '../shell/PublicShell';
import { Modal } from '../ui/Modal';
import { mosaicTiles } from '../ui/Mosaic';
import { PublicLink } from '../ui/PublicLink';
import { useToast } from '../ui/toast';
import { LinkedInMemberPost, type MemberPostState } from './LinkedInMemberPost';

// ------------------------------------------------------------------------------- reference icons

function IconCopy(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

function IconSend(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function IconPrint(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M6 9V3h12v6" />
      <rect x="3" y="9" width="18" height="9" rx="2" />
      <path d="M6 14h12v7H6z" />
    </svg>
  );
}

function IconX(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

// ------------------------------------------------------------------------------- shared plumbing

const LANG_KEY = 'hpw_lang';

function storedLang(): PublicLanguage {
  try {
    return localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'tr';
  } catch {
    return 'tr';
  }
}

/**
 * `?lang=` — the language a LINK asks for.
 *
 * The record's address does not change: same `/c/:code`, same canonical URL, same QR, same
 * credential. This is one narrow query parameter so that «open this certificate in English» is a
 * thing a link can say, and so that a visitor who switches language can hand the address to someone
 * else and have them see the same document.
 *
 * Fail-closed on anything else: `?lang=de` is NOT read as Turkish-by-default-so-near-enough. It is
 * reported as a language this page does not have, and the page shows its own — never the other
 * language's document under the asked-for language's name.
 */
type AskedLanguage =
  | { kind: 'none' }
  | { kind: 'ok'; lang: PublicLanguage }
  | { kind: 'unsupported'; value: string };

export function askedLanguage(value: string | null): AskedLanguage {
  if (value === null || value === '') return { kind: 'none' };
  if (value === 'tr' || value === 'en') return { kind: 'ok', lang: value };
  return { kind: 'unsupported', value };
}

/**
 * `copyText` — the toast is raised in the same turn as the click, before the async clipboard
 * permission resolves, because the reference does not make a visitor wait for a permission prompt
 * to learn that their click registered.
 */
function useCopy(): (text: string, message?: string) => void {
  const toast = useToast();
  return useCallback(
    (text: string, message = 'Kopyalandı') => {
      if (message) toast(message);
      const fallback = (): boolean => {
        const ta = document.createElement('textarea');
        ta.value = String(text ?? '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try {
          ok = document.execCommand('copy');
        } catch {
          ok = false;
        }
        ta.remove();
        return ok;
      };
      const write = navigator.clipboard?.writeText?.(String(text ?? ''));
      if (!write) {
        if (!fallback() && message) toast('Panoya yazılamadı — metni elle kopyalayın');
        return;
      }
      write.catch(() => {
        if (!fallback() && message) toast('Panoya yazılamadı — metni elle kopyalayın');
      });
    },
    [toast],
  );
}

// --------------------------------------------------------------------------------- lookup form

interface LookupFormProps {
  placeholder: string;
  submit: string;
  initial?: string;
}

/**
 * The reference's lookup form is uncontrolled: it writes the code straight into the hash on
 * submit and never re-renders while the visitor types. Keeping it uncontrolled here keeps the
 * caret and the input's own value attribute behaving the way the reference's does.
 */
function LookupForm({ placeholder, submit, initial }: LookupFormProps): ReactElement {
  const goToCredential = useGoToCredential();
  /* A ref rather than a `name`: the reference's input carries no name attribute, and an attribute
     this surface adds is an attribute the reference dump does not have. */
  const field = useRef<HTMLInputElement>(null);
  return (
    <form
      className="form"
      id="lookupForm"
      onSubmit={(event) => {
        event.preventDefault();
        const v = field.current?.value.trim() ?? '';
        if (v) goToCredential(v);
      }}
    >
      <input
        className="input"
        id="lookupCode"
        placeholder={placeholder}
        {...(initial === undefined ? {} : { defaultValue: initial })}
        autoComplete="off"
        aria-label="Credential ID"
        ref={field}
      />
      <button className="btn btn-primary" type="submit">
        {submit}
      </button>
    </form>
  );
}

// --------------------------------------------------------------------------- LinkedIn surfaces

interface HelperProps {
  credential: DemoCredential;
  lang: PublicLanguage;
  settings: DemoSettings;
  popupBlocked: boolean;
  onClose: () => void;
}

/**
 * «LinkedIn profilinize ekleyin» — `openLinkedInHelper` (`m05-linkedin.js:55`).
 *
 * The honest fallback, and the only profile flow this product may claim today: LinkedIn does not
 * pre-fill certification fields for third parties, so the helper opens the real form and walks the
 * visitor through copying each field. Nothing here writes to a profile (binding rules 14 and 15).
 */
export function LinkedInHelperModal({
  credential,
  lang,
  settings,
  popupBlocked,
  onClose,
}: HelperProps): ReactElement {
  const L = HELPER_I18N[lang];
  const copy = useCopy();
  const toast = useToast();
  const commands = useV07Commands();
  const fields = useMemo(() => linkedInFields(credential, lang, settings), [credential, lang, settings]);
  /*
   * The same link the card's button opened, rebuilt here so «formu yeniden aç» reopens the FILLED
   * form rather than the empty one. `addToProfileLinkFor` is the single builder both surfaces use.
   */
  const addToProfile = useMemo(
    () =>
      addToProfileLinkForCredential(credential, credTitle(credential, lang), credential.release, settings),
    [credential, lang, settings],
  );
  const [copied, setCopied] = useState<number[]>([]);
  const [current, setCurrent] = useState(0);
  const [saved, setSaved] = useState(false);
  const expired = credentialViewState(credential) === 'expired';
  const done = copied.length;
  /*
   * The resolved «Veren kuruluş», so the helper's guidance, its buttons, the preview and the link
   * all name the same organisation — and none of them names the issuer when this record's own
   * organisation cannot be read.
   */
  const organization = credentialIssuingOrganization(credential.release, settings);
  const unreadableIssuer = organization.source === 'unreadable';
  const issuerName = organization.organizationName;

  const markCopied = (index: number): void => {
    const next = copied.includes(index) ? copied : [...copied, index];
    setCopied(next);
    const after = fields.findIndex((_, j) => j > index && !next.includes(j));
    setCurrent(after >= 0 ? after : fields.findIndex((_, j) => !next.includes(j)));
  };

  return (
    <Modal drawer wide label={L.title} onClose={onClose}>
      <div className="card-head">
        <div>
          <span className="eyebrow">{L.eyebrow}</span>
          <h2 style={{ marginTop: '6px' }}>{L.title}</h2>
        </div>
        <button className="icon-btn" type="button" data-close="1" aria-label={L.close} onClick={onClose}>
          <IconX />
        </button>
      </div>
      <div className="card-pad li-helper" lang={lang}>
        {popupBlocked ? (
          <div
            className="warn-box"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <span>{L.blocked}</span>
            <a
              className="btn btn-dark btn-sm"
              href={addToProfile.url ?? undefined}
              aria-disabled={addToProfile.url ? undefined : true}
              target="_blank"
              rel="noopener"
              onClick={() => {
                if (addToProfile.url)
                  void commands.recordEngagement({
                    code: credential.publicCode,
                    kind: 'linkedin_profile_add_started',
                  });
              }}
            >
              {L.reopen}
            </a>
          </div>
        ) : null}
        {expired ? (
          <div className="warn-box">
            <b>{`⚠ ${L.expiredWarn}`}</b>
          </div>
        ) : null}
        <p className="muted" style={{ fontSize: '13.5px' }}>
          {L.intro}
        </p>
        <div className="warn-box" style={{ display: 'grid', gap: '8px' }}>
          <span>
            <b>{`${L.fields.org ?? ''}:`}</b>
            {` ${unreadableIssuer ? L.issuerUnreadable : L.issuerWarn(issuerName)}`}
          </span>
          {unreadableIssuer ? null : (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="liCopyIssuer"
                onClick={() => copy(issuerName, L.copied)}
              >
                <IconCopy />
                {L.copyIssuer}
              </button>
              {/* Only when THIS organisation has a Page of its own: borrowing the issuer's would
                  send the holder to the wrong company. */}
              {organization.pageUrl ? (
                <a
                  className="btn btn-secondary btn-sm"
                  href={organization.pageUrl}
                  target="_blank"
                  rel="noopener"
                  id="liOpenPage"
                >
                  {L.openPage}
                </a>
              ) : null}
            </div>
          )}
        </div>
        <div className="li-progress">
          <b id="liProgressText">{L.progress(done, fields.length)}</b>
          <div className="progress">
            <i id="liProgressBar" style={{ width: `${Math.round((done / fields.length) * 100)}%` }} />
          </div>
        </div>
        <div className="li-fields" id="liFields">
          {fields.map((f, i) => (
            <div
              className={`li-field ${copied.includes(i) ? 'is-copied' : i === current ? 'is-current' : ''}`}
              data-idx={i}
              key={f.key}
            >
              <div className="field">
                <label htmlFor={`liF${i}`}>{f.label}</label>
                <input className="input input-sm" id={`liF${i}`} readOnly value={f.value} />
              </div>
              <button
                className="btn btn-secondary btn-sm li-copy"
                type="button"
                data-copy={i}
                title={L.copyNext}
                aria-label={`${f.label} · ${L.copyNext}`}
                onClick={() => {
                  copy(f.value, `${f.label}: ${L.copied}`);
                  markCopied(i);
                }}
              >
                <IconCopy />
                <span>{copied.includes(i) ? L.copied : L.copy}</span>
              </button>
            </div>
          ))}
        </div>
        {credential.expiresAt ? null : (
          <div className="info-box" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span>∞</span>
            <span>{L.noExpiry}</span>
          </div>
        )}
        <div className="li-preview-wrap">
          <div className="label" style={{ marginBottom: '8px' }}>
            {L.previewTitle}
          </div>
          <div className="li-preview" lang={lang}>
            <div className="li-logo" title={L.mockPreview}>
              <span className="mark" />
              <span className="li-logo-note">{lang === 'en' ? 'representative' : 'temsilî'}</span>
            </div>
            <div className="li-body">
              <b>{credTitle(credential, lang)}</b>
              <span>{issuerName}</span>
              <span className="muted">
                {`${L.previewIssued} ${fmtDate(credential.issueDate, lang, 'month')} · ${
                  credential.expiresAt
                    ? `${L.previewExp} ${fmtDate(credential.expiresAt, lang, 'month')}`
                    : L.previewNoExp
                }`}
              </span>
              <span className="muted">{`Credential ID ${credential.publicCode}`}</span>
              <PublicLink
                to={`/c/${credential.publicCode}`}
                target="_blank"
                rel="noopener"
                className="li-show"
              >
                {`${L.previewShow} ↗`}
              </PublicLink>
            </div>
          </div>
          <p className="help" style={{ marginTop: '8px' }}>
            {`${L.mockPreview} ${L.previewNote}`}
          </p>
        </div>
        <div id="liSaved" className="ok-box" hidden={!saved}>
          {L.savedToast}
        </div>
      </div>
      <div className="card-foot li-actions">
        <button
          className="btn btn-secondary"
          type="button"
          id="liAll"
          onClick={() => {
            copy(linkedInAllText(credential, lang, settings), L.allCopied);
            setCopied(fields.map((_, i) => i));
            setCurrent(-1);
          }}
        >
          <IconCopy />
          {L.all}
        </button>
        <a
          className="btn li-btn"
          href={addToProfile.url ?? undefined}
          aria-disabled={addToProfile.url ? undefined : true}
          target="_blank"
          rel="noopener"
          id="liOpen"
          /* The navigation is the anchor's own and is not intercepted; the measurement rides along. */
          onClick={() => {
            if (addToProfile.url)
              void commands.recordEngagement({
                code: credential.publicCode,
                kind: 'linkedin_profile_add_started',
              });
          }}
        >
          <span className="in">in</span>
          {L.open}
        </a>
        <button
          className="btn btn-ghost"
          type="button"
          id="liRestart"
          onClick={() => {
            setCopied([]);
            setCurrent(0);
          }}
        >
          {L.restart}
        </button>
        <button
          className="btn btn-primary"
          type="button"
          id="liSavedBtn"
          onClick={() => {
            void commands.recordLinkedInStep({ credentialId: credential.id, step: 'saved' });
            setSaved(true);
            toast(L.savedToast);
            window.setTimeout(onClose, 1400);
          }}
        >
          {L.saved}
        </button>
        <button className="btn btn-ghost" type="button" data-close="1" onClick={onClose}>
          {L.close}
        </button>
      </div>
    </Modal>
  );
}

/**
 * The link-card preview both share dialogs show.
 *
 * On a server-rendered page it shows the SERVER's card — the exact image the page's own `og:image`
 * names, so what the visitor previews is what LinkedIn fetches. On the demo, where no server wrote
 * a head, it composes the picture in the browser exactly as it always did.
 *
 * The CSP is `img-src 'self' data: blob:` and the API is another origin, so the server card is
 * fetched (`connect-src` already names that origin) and shown as a `blob:` URL. Nothing about the
 * policy was widened for this.
 *
 * Loading and failure are STATES, not silence. The previous code swallowed every error and left an
 * empty frame; here a failure says so in the visitor's own language and offers a working retry.
 */
/**
 * The language to ask the share image for — and nothing when this record has only one.
 *
 * A single-language record's address stays byte for byte what it was, so an existing deployment's
 * share window requests precisely what it requested before. Only a record that is actually sealed
 * in two languages carries the choice.
 */
function shareLocale(c: DemoCredential, lang: PublicLanguage): 'tr' | 'en' | undefined {
  const sealed = c.release.sealedLanguages;
  return sealed && sealed.length > 1 ? lang : undefined;
}

function OgPreview({
  credential,
  svg,
  strings,
  imgId,
  lang,
  memberPost = false,
}: {
  credential: DemoCredential;
  svg: string;
  strings: ShareStrings;
  imgId: string;
  /** The language the reader is on: the heading must be the name of the document being shared. */
  lang: PublicLanguage;
  /**
   * True where this frame is labelled «the image your post will carry».
   *
   * Then it must be that image. The link card and the member post are two pictures whenever the
   * issuer has arranged one for LinkedIn, and showing the first under the second's label is a
   * preview of something the member is not about to publish.
   */
  memberPost?: boolean;
}): ReactElement {
  /*
   * Read once per record: the head is written by the server before the bundle runs and never
   * changes, and the code is what decides both whether the head is this record's and — when the
   * server's read timed out and wrote no head — what the address is derived from.
   */
  const server = useMemo(() => serverShareCard(credential.publicCode), [credential.publicCode]);
  const shown = useMemo(
    () =>
      memberPost
        ? (memberPostShareImage(server, shareLocale(credential, lang)) ?? server?.imageUrl ?? null)
        : (server?.imageUrl ?? null),
    /* `lang` is in here now: the picture the post will upload changes with the language, so the
       address must be recomputed when the reader switches — that is the whole point of it. */
    [memberPost, server, credential, lang],
  );
  const remote = useShareCardImage(shown, strings.previewFailed);

  /* The demo path, unchanged: compose from the record's own SVG in the tab. */
  const [composed, setComposed] = useState<string | null>(null);
  const [composeFailed, setComposeFailed] = useState(false);
  const [composeAttempt, setComposeAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `composeAttempt` is not read here; it is the retry signal, and bumping it is what re-runs the composition
  useEffect(() => {
    if (server) return;
    let live = true;
    setComposeFailed(false);
    buildOgImage(credential, svg)
      .then((d) => {
        if (live) setComposed(d);
      })
      .catch(() => {
        if (live) setComposeFailed(true);
      });
    return () => {
      live = false;
    };
  }, [credential, svg, server, composeAttempt]);

  /*
   * The demo keeps its exact previous rendering: the `<img>` element is always present and simply
   * has no `src` until the composition finishes. That is what the frozen visual acceptance measured,
   * so a loading placeholder must not appear there. What the demo did NOT do before is admit a
   * failed composition — that was the swallowed error, and it now surfaces like any other.
   */
  const failed = server ? remote.status === 'error' : composeFailed;
  const pending = server && remote.status === 'loading';
  const src = server ? remote.src : composed;
  /* The heading must not disagree with the link card, so the server's own `og:title` wins. */
  const heading = server?.title ?? `${credTitle(credential, lang)} · ${displayName(credential)}`;

  return (
    <div className="og-preview" data-hpw-og-source={server ? 'server' : 'composed'}>
      {failed || pending ? (
        <div
          className="og-preview-state"
          data-hpw-og-state={failed ? 'error' : 'loading'}
          role={failed ? 'alert' : 'status'}
        >
          <span>{failed ? strings.previewFailed : strings.previewLoading}</span>
          {failed ? (
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              data-hpw-og-retry="1"
              onClick={() => {
                if (server) remote.retry();
                else setComposeAttempt((n) => n + 1);
              }}
            >
              {strings.previewRetry}
            </button>
          ) : null}
        </div>
      ) : (
        <img id={imgId} alt="OG önizleme" width="1200" height="630" src={src ?? undefined} />
      )}
      <div className="og-meta">
        <b>{heading}</b>
        <span>{publicUrl(credential).replace(/^https?:\/\//, '')}</span>
      </div>
    </div>
  );
}

interface ShareProps {
  credential: DemoCredential;
  lang: PublicLanguage;
  svg: string;
  popupBlocked: boolean;
  allowDownload: boolean;
  /** Called only when the reader presses the dialog's own LinkedIn control. */
  onOpenLinkedIn: () => void;
  onClose: () => void;
}

/**
 * «LinkedIn'de gönderi olarak paylaş» — `openShareModal` (`m05-linkedin.js:102`).
 *
 * A feed post is not a profile entry and the dialog says so in its first box; conflating the two
 * is exactly the claim binding rule 15 forbids. For an organization record the page-admin caveat
 * is appended, because nothing here can post on a company page's behalf.
 *
 * The dialog has two faces, and which one it shows is decided by the deployment, not by taste:
 *
 * - Where this deployment really has the LinkedIn integration, the dialog IS the composer, in ONE
 *   view: the picture the post will carry, the text edited in place, the account it will post as,
 *   that the post is public, and the single control that publishes. There is no separate review
 *   page to go forward to and come back from. The old «open LinkedIn's own box» control is not
 *   offered beside it: two buttons that both said LinkedIn and did different things is the
 *   confusion this replaces.
 * - Where it does not — an unconfigured deployment, and the serverless demo — nothing changes.
 *   The manual helpers stay exactly as they were, the link button says plainly that it shares the
 *   link only, and the sentence about pasting the text by hand stays with them, because there the
 *   text really does have to be pasted by hand.
 *
 * `enabled` is false until the status answer says otherwise, so the demo never draws a frame of
 * the other dialog.
 *
 * The two explanations sit at the FOOT of the dialog, under the copy/download row: what a feed
 * post is and is not, and what the button above actually does. They used to open the dialog, so a
 * member met two paragraphs before the picture they came to look at.
 *
 * Exported so the flow it promises can be driven as a whole — the composer, the publish block and
 * the request it sends are one behaviour and are tested as one.
 */
export function ShareModal({
  credential,
  lang,
  svg,
  popupBlocked,
  allowDownload,
  onOpenLinkedIn,
  onClose,
}: ShareProps): ReactElement {
  const S = SHARE_I18N[lang];
  const copy = useCopy();
  const toast = useToast();
  const url = publicUrl(credential);
  /* Copying the suggested text and copying the link are acts this dialog can see. Where either
     then went is not measured, and the history's own wording says so. */
  const interaction = useInteractionRecorder(credential.publicCode);
  const isOrg = (credential.subjectSnapshot as { subjectType?: string }).subjectType === 'organization';
  /*
   * The suggested post, with the record's own verification address on its own line.
   *
   * The address is part of the DEFAULT text rather than something appended behind the member's
   * back: the server appends it only when the text does not already carry it, so what the member
   * reads in this box is, character for character, what LinkedIn receives — and it appears exactly
   * once. Editing or deleting the line is still theirs to do; the server puts it back, and the
   * dialog says so where that happens.
   */
  const [text, setText] = useState(
    () =>
      rememberedShareText(credential.publicCode, lang) ?? `${suggestedShareText(credential, lang)}\n${url}`,
  );
  const [member, setMember] = useState<MemberPostState>({
    enabled: false,
    availability: 'checking',
    connected: false,
    memberName: null,
    canonicalUrl: null,
    busy: false,
  });
  const api = member.enabled;
  /*
   * When the link share is offered, and what it is called when it is.
   *
   * It is the right offer on a deployment that answered «not configured», and it is NOT offered
   * while the answer is still coming — a link that appears for half a second and then disappears
   * reads as the product changing its mind. Where the status could not be established it is
   * offered, but as an ALTERNATIVE: the publish block above it says the status is unknown, and
   * this line says what the link does instead, so nothing substitutes for the post silently.
   */
  const linkShare = member.availability === 'disabled' || member.availability === 'unavailable';
  /*
   * The post, as LinkedIn will receive it.
   *
   * Not necessarily the contents of the text box: the server trims the text and appends the
   * record's verification address when the member has not written it themselves. The default text
   * carries the address, so the two are normally identical — and where the member has deleted it,
   * the difference is SHOWN rather than sprung on them after they press publish. The address the
   * server gave for THIS draft wins as soon as there is a draft; before that the record's own
   * public address — the value the server derives the draft's from — stands in, so the length and
   * emptiness checks are already honest.
   */
  const canonicalUrl = member.canonicalUrl ?? url;
  const post = useMemo(() => buildCommentary({ text, canonicalUrl }), [text, canonicalUrl]);
  const textProblem = post.ok
    ? null
    : { empty: S.textEmpty, too_long: S.textTooLong, control_characters: S.textControl }[post.reason];
  /* Shown only when the post really differs from the box — see `finalTextNote`. */
  const finalText = post.ok && post.commentary !== text.trim() ? post.commentary : null;

  return (
    <Modal wide label={S.title} onClose={onClose}>
      <div className="card-head">
        <h2>{S.title}</h2>
        <button className="icon-btn" type="button" data-close="1" aria-label={S.close} onClick={onClose}>
          <IconX />
        </button>
      </div>
      <div className="card-pad" lang={lang}>
        {popupBlocked ? <div className="warn-box">{S.blocked}</div> : null}
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>
            {api ? S.imageLabel : S.preview}
          </div>
          {/*
           * Rendered once, in one place, for both steps. Moving this element between two branches
           * would remount it and refetch the server's card, so the picture the member is about to
           * confirm would blink away underneath them.
           */}
          <OgPreview
            credential={credential}
            svg={svg}
            strings={S}
            imgId="ogImg"
            lang={lang}
            memberPost={api}
          />
          {/* The link-card wording belongs to the manual path; the API path is not about metadata. */}
          {api ? null : (
            <p className="help" style={{ marginTop: '6px' }}>
              {S.previewNote}
            </p>
          )}
        </div>
        <div className="field">
          <label htmlFor="shareText">{S.suggested}</label>
          <textarea
            className="textarea"
            id="shareText"
            style={{ minHeight: '84px' }}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              rememberShareText(credential.publicCode, lang, event.target.value);
            }}
          />
          {api ? (
            <p className="help">
              {S.editHint} {S.linkHint}
            </p>
          ) : null}
          {/* Said where the member is typing, in their own words, before any button is pressed. */}
          {api && textProblem ? (
            <div className="warn-box" role="alert" data-hpw-share-text-error={post.ok ? '' : post.reason}>
              {textProblem}
            </div>
          ) : null}
          {/*
           * The finished post, shown ONLY where it differs from the box.
           *
           * The default text already carries the verification address, so normally the box IS the
           * post and repeating it below would be noise. When the member deletes that line the
           * server puts it back — and a member who is about to press publish is entitled to read
           * the text that will actually be sent, built here by the same rule the server uses.
           */}
          {api && finalText ? (
            <>
              <div className="label" style={{ margin: '10px 0 6px' }}>
                {S.finalTextLabel}
              </div>
              <p className="share-review-text" data-hpw-review-text="1" data-testid="shareFinalText">
                {finalText}
              </p>
              <p className="help" style={{ marginTop: '6px' }}>
                {S.finalTextNote}
              </p>
            </>
          ) : null}
        </div>
        {/*
         * Renders only where the deployment really has the LinkedIn integration configured, so an
         * unconfigured deployment and the serverless demo show exactly what they showed before.
         * Its position in this tree is fixed across both steps: it must never be remounted.
         */}
        <LinkedInMemberPost
          publicCode={credential.publicCode}
          text={text}
          strings={S}
          publishable={post.ok}
          onStateChange={setMember}
        />
      </div>
      <div className="card-foot" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            id="shCopyText"
            onClick={() => {
              interaction.note('text_copied');
              copy(text);
            }}
          >
            <IconCopy />
            {S.copyText}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            id="shCopyLink"
            onClick={() => {
              interaction.note('link_copied');
              copy(url);
            }}
          >
            <IconCopy />
            {S.copyLink}
          </button>
          {allowDownload ? (
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="shPng"
              onClick={() => {
                credentialPngDataUrl(svg, 1)
                  .then((data) => {
                    if (downloadDataUrl(data, `${credential.publicCode}.png`)) toast('PNG hazırlandı');
                  })
                  .catch((e: Error) => toast(`PNG üretilemedi: ${e.message}`));
              }}
            >
              {S.png}
            </button>
          ) : null}
        </div>
        {/*
         * The manual way to share, offered only where there is no other way.
         *
         * It opens LinkedIn's own composer with the page address and nothing else, which is a
         * different outcome from publishing the post — and standing beside the publish flow, with
         * both buttons carrying the LinkedIn mark, that difference was invisible. Where the
         * integration is configured it is not rendered at all; where it is not, it stays exactly
         * as it was and now says what it actually does.
         *
         * The `href` is kept so a middle-click or Ctrl-click behaves like any link; a plain click
         * is handled here instead, purely so a browser that refuses the new tab can be reported
         * (`S.blocked`) rather than looking like nothing happened.
         */}
        {linkShare ? (
          <>
            {/*
             * Why this is the only thing on offer — in both states, because in both states the
             * member came for an image post and is being handed something else. `unavailable` is
             * «we could not ask», and the publish block above already says so; `disabled` is the
             * deployment's own answer, and nothing else on this screen says it at all.
             */}
            <p className="help" data-hpw-link-alternative="1">
              {member.availability === 'unavailable' ? S.linkAlternative : S.linkOnlyDeployment}
            </p>
            <a
              className="btn li-btn"
              href={linkedInShareUrl(url)}
              target="_blank"
              rel="noopener"
              id="shOpenLinkedIn"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onOpenLinkedIn();
              }}
            >
              <span className="in">in</span>
              {S.open}
            </a>
          </>
        ) : null}
        {/*
         * The two explanations, at the foot of the dialog.
         *
         * They used to open it: a member came to look at a picture and read two paragraphs first.
         * They are still here — what a feed post is and is not, and what the button above does —
         * but underneath the row of actions, on their own full-width line so the row above keeps
         * its shape at every width.
         */}
        <div className="share-notes" data-hpw-share-notes="1">
          <p>
            {isOrg ? `${S.note} ${S.orgNote}` : S.note}
            {/*
             * Only where the text really has to be pasted by hand. Where the integration publishes
             * the post itself, this sentence would describe a step that no longer exists.
             */}
            {api ? null : (
              <>
                {' '}
                <b>{S.noPrefill}</b>
              </>
            )}
          </p>
          {api ? <p>{S.postNote}</p> : null}
        </div>
      </div>
    </Modal>
  );
}

/** «Paylaşım önizlemesi» — `openOgPreview` (`m05-linkedin.js:138`); the asset contract, verbatim. */
function OgPreviewModal({
  credential,
  svg,
  lang,
  onClose,
}: {
  credential: DemoCredential;
  svg: string;
  lang: PublicLanguage;
  onClose: () => void;
}): ReactElement {
  return (
    <Modal wide label="Paylaşım önizlemesi" onClose={onClose}>
      <div className="card-head">
        <h2>Paylaşım önizlemesi</h2>
        <button className="icon-btn" type="button" data-close="1" aria-label="Kapat" onClick={onClose}>
          <IconX />
        </button>
      </div>
      <div className="card-pad">
        <OgPreview credential={credential} svg={svg} strings={SHARE_I18N[lang]} imgId="ogImg2" lang={lang} />
        <p className="help">
          {'Asset sözleşmesi: '}
          <code>/c/:code</code>
          {' · '}
          <code>/c/:code/image.png</code>
          {' · '}
          <code>/c/:code/print</code>
          {' · '}
          <code>/c/:code/og.png</code>
          {
            " (1200×630). Üretimde bu metadata JavaScript sonradan çalıştırılarak değil, public route'un ilk HTML cevabında server-side üretilmelidir."
          }
        </p>
      </div>
    </Modal>
  );
}

const SIM_NOTE = 'LinkedIn tarafından onaylanmış Profile Edit API erişimi ve üye yetkilendirmesi gerektirir.';

function SimBadge(): ReactElement {
  return (
    <div className="sim-badge" role="note">
      <b>PROTOTİP SİMÜLASYONU</b>
      {` — ${SIM_NOTE}`}
    </div>
  );
}

const API_SIM_STEPS = [
  'İstek hazırlanıyor',
  'Üye yetkisi doğrulanıyor',
  "LinkedIn'e gönderiliyor",
  'Profil kaydı oluşturuluyor',
  'Tamamlandı',
];

/**
 * «LinkedIn Profilime Ekle» ön onayı — `openLinkedInApiPreconfirm` (`m05b-linkedin-api.js:238`).
 *
 * Nothing is sent anywhere. The fields are read-only on purpose: a correction belongs in the
 * credential correction flow, which produces a new revision, not in a share dialog.
 *
 * API GAP: the real flow needs an approved LinkedIn Profile Edit API grant, a member OAuth
 * authorization and a server-side `CredentialLinkedInSync` row holding the returned certification
 * id — none of which exists, which is why this screen is honest about being a simulation.
 */
function LinkedInApiPreconfirmModal({
  credential,
  settings,
  lang,
  onClose,
}: {
  credential: DemoCredential;
  settings: DemoSettings;
  /** The language the reader is on — what would be sent is the document they are looking at. */
  lang: PublicLanguage;
  onClose: () => void;
}): ReactElement {
  const commands = useV07Commands();
  const [step, setStep] = useState(-1);
  const [finished, setFinished] = useState(false);
  const url = publicUrl(credential);

  const row = (label: string, value: string): ReactElement => (
    <div className="kv" key={label}>
      <span className="k">{label}</span>
      <span className="v">{value}</span>
    </div>
  );

  const run = async (): Promise<void> => {
    /*
     * Rule 15: nothing here is «added automatically». The request is recorded, the steps below are
     * the reference's own animation, and the second event says «simulated» in its own payload.
     */
    const requested = await commands.recordLinkedInStep({
      credentialId: credential.id,
      step: 'api_sync_requested',
    });
    for (let i = 0; i < API_SIM_STEPS.length; i++) {
      setStep(i);
      await new Promise((r) => window.setTimeout(r, 420));
    }
    await commands.recordLinkedInStep({
      credentialId: credential.id,
      step: 'api_sync_simulated',
      ...(requested.correlationId ? { correlationId: requested.correlationId } : {}),
    });
    setFinished(true);
  };

  return (
    <Modal wide label="LinkedIn API ön onayı" onClose={onClose}>
      <div className="card-head">
        <div>
          <span className="eyebrow">LinkedIn · Onaylı API ile Otomatik Ekleme Simülasyonu</span>
          <h2 style={{ marginTop: '6px' }}>LinkedIn Profilime Ekle</h2>
        </div>
        <button className="icon-btn" type="button" data-close="1" aria-label="Kapat" onClick={onClose}>
          <IconX />
        </button>
      </div>
      <div className="card-pad stack" style={{ gap: '12px' }} id="liApiBody">
        <SimBadge />
        {finished ? (
          <>
            <div className="ok-box">
              <b>Prototip simülasyonu tamamlandı.</b>
              {' Gerçek kayıt için LinkedIn onaylı API erişimi gerekir.'}
            </div>
            <p className="help">
              {
                "Bu akışta LinkedIn'e hiçbir istek gönderilmedi, hiçbir profil kaydı oluşturulmadı. Gerçek entegrasyonda LinkedIn'in döndürdüğü certification ID "
              }
              <code>CredentialLinkedInSync.linkedinCertificationId</code>
              {' alanında saklanır.'}
            </p>
          </>
        ) : (
          <>
            <div className="kv-list">
              {row('Sertifika adı', credTitle(credential, lang))}
              {row(
                'Veren kuruluş',
                credential.release.issuerProfileSnapshot.displayName || settings.issuerDisplayName,
              )}
              {/* The same verified configuration the link builder reads, resolved against THIS
                  issuer's own Page — an issuer with a different Page keeps the placeholder. */}
              {row(
                'LinkedIn organization referansı',
                (() => {
                  const id = verifiedOrganizationIdForPage(
                    credential.release.issuerProfileSnapshot.linkedinPageUrl || settings.linkedinPageUrl,
                  );
                  return id ? `urn:li:organization:${id}` : 'urn:li:organization:HPW-PROTOTYPE-PLACEHOLDER';
                })(),
              )}
              {row('Veriliş tarihi', fmtDate(credential.issueDate, 'tr', 'month'))}
              {row(
                'Bitiş',
                credential.expiresAt
                  ? fmtDate(credential.expiresAt, 'tr', 'month')
                  : 'Süresiz (This credential does not expire)',
              )}
              {row('Credential ID', credential.publicCode)}
              {row('Credential URL', url)}
            </div>
            <p className="help">
              Alanlar credential kaydından salt-okunur gelir; bu ekranda düzenlenemez. Değişiklik gerekiyorsa
              credential düzeltme akışı kullanılır.
            </p>
            <div id="liApiProgress" hidden={step < 0}>
              <ol className="sim-steps" aria-live="polite">
                {API_SIM_STEPS.map((s, j) => (
                  <li className={j < step ? 'is-done' : j === step ? 'is-active' : ''} key={s}>
                    {j < step ? `✓ ${s}` : s}
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </div>
      {finished ? (
        <div className="card-foot">
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <a
              className="btn btn-secondary btn-sm"
              href="https://www.linkedin.com/in/"
              target="_blank"
              rel="noopener"
            >
              LinkedIn profilimi aç ↗
            </a>
            <PublicLink
              className="btn btn-secondary btn-sm"
              to={`/c/${credential.publicCode}`}
              target="_blank"
              rel="noopener"
            >
              HPW doğrulama sayfasını aç ↗
            </PublicLink>
            {/*
             * The same correction `4078470` made in the share dialog, in the one place it was
             * missed. This opens LinkedIn's composer with the page address, which produces a link
             * card — not the image post the old label promised. The flow is unchanged and this
             * helper stays what it is; only the promise is brought back to what it does.
             */}
            <a
              className="btn btn-secondary btn-sm"
              href={linkedInShareUrl(url)}
              target="_blank"
              rel="noopener"
            >
              {"LinkedIn'de yalnızca bağlantıyı paylaş ↗"}
            </a>
            <button className="btn btn-ghost btn-sm" type="button" data-close="1" onClick={onClose}>
              Kapat
            </button>
          </div>
        </div>
      ) : (
        <div className="card-foot">
          <button className="btn btn-ghost" data-close="1" type="button" onClick={onClose}>
            Kapat
          </button>
          <button
            className="btn btn-primary"
            id="liApiGo"
            type="button"
            disabled={step >= 0}
            onClick={() => {
              void run();
            }}
          >
            LinkedIn Profilime Ekle
          </button>
        </div>
      )}
    </Modal>
  );
}

/** «LinkedIn ile Bağlan» — `openLinkedInAuthorizePrototype` (`m05b-linkedin-api.js:211`). */
function LinkedInAuthorizeModal({
  credential,
  settings,
  lang,
  onGranted,
  onClose,
}: {
  credential: DemoCredential;
  settings: DemoSettings;
  /** The language the reader is on; see `LinkedInApiPreconfirmModal`. */
  lang: PublicLanguage;
  onGranted: () => void;
  onClose: () => void;
}): ReactElement {
  const commands = useV07Commands();
  return (
    <Modal wide label="LinkedIn yetkilendirme prototipi" onClose={onClose}>
      <div className="card-head">
        <div>
          <span className="eyebrow">LinkedIn · Yetkilendirme prototipi</span>
          <h2 style={{ marginTop: '6px' }}>LinkedIn ile Bağlan</h2>
        </div>
        <button className="icon-btn" type="button" data-close="1" aria-label="Kapat" onClick={onClose}>
          <IconX />
        </button>
      </div>
      <div className="card-pad stack" style={{ gap: '14px' }}>
        <SimBadge />
        <div className="info-box">
          <b>Uygulama:</b>
          {' Happy Place to Work'}
          <br />
          <b>İstenen işlem:</b>
          {' LinkedIn profilinizin «Lisanslar ve Sertifikalar» bölümüne sertifika kaydı oluşturmak.'}
        </div>
        <div className="card" style={{ boxShadow: 'none' }}>
          <div className="card-pad stack" style={{ gap: '6px' }}>
            <b>Eklenecek credential</b>
            <span>{credTitle(credential, lang)}</span>
            <span className="small muted">
              {`${settings.linkedinIssuerName || 'Happy Place to Work Türkiye'} · Credential ID ${credential.publicCode}`}
            </span>
            <span className="small muted">
              {`${fmtDate(credential.issueDate, 'tr', 'month')}${
                credential.expiresAt ? ` – ${fmtDate(credential.expiresAt, 'tr', 'month')}` : ' · süresiz'
              }`}
            </span>
          </div>
        </div>
        <p className="help">
          {
            "Bu ekran gerçek bir OAuth penceresi değildir. Gerçek yetkilendirme, onaylı API erişimi alındığında LinkedIn'in kendi sayfasında ve server-side authorization code akışıyla yapılır."
          }
        </p>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onClose}>
          İptal
        </button>
        <button
          className="btn btn-primary"
          id="liAuthGo"
          type="button"
          onClick={() => {
            void commands.recordLinkedInStep({
              credentialId: credential.id,
              step: 'member_authorized',
            });
            onGranted();
          }}
        >
          İzin ver ve devam et
        </button>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------------------- the card body

type Dialog = 'helper' | 'share' | 'og' | 'authorize' | 'preconfirm' | null;

interface CardProps {
  credential: DemoCredential;
  lang: PublicLanguage;
  db: DemoDatabase;
  settings: DemoSettings;
  published: boolean;
}

function CredentialCard({ credential: c, lang, db, settings, published }: CardProps): ReactElement {
  const L = PUBLIC_I18N[lang];
  const copy = useCopy();
  const toast = useToast();
  const commands = useV07Commands();
  /*
   * The acts this page performs, recorded where they survive a refresh (PO 7.12 §3).
   *
   * Each is named at the level it can be seen from here: the page was OPENED, a download was
   * ASKED for, a print WINDOW was opened, a link was COPIED. Nothing here identifies the visitor
   * and nothing here can become a system record — the server's allow-list is the same list.
   */
  const interaction = useInteractionRecorder(c.publicCode);
  usePageViewed(c.publicCode, 'public_viewed');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const T = credTerms(c, lang);
  const s = credentialViewState(c);
  const isOrg = (c.subjectSnapshot as { subjectType?: string }).subjectType === 'organization';
  const issuerName = c.release.issuerProfileSnapshot.displayName;
  const desc = credDescription(c, lang);
  const crit = credCriteria(c, lang);
  const stmt = credIssuerStatement(c, lang);
  const perm = c.release.permissions;
  const issuerSite = c.release.issuerProfileSnapshot.website || settings.issuerWebsite;
  const issuerLi = c.release.issuerProfileSnapshot.linkedinPageUrl || settings.linkedinPageUrl;
  const live = s !== 'revoked' && s !== 'superseded';
  /*
   * WHETHER THIS RECORD HAS A DOCUMENT IN THE LANGUAGE ON SCREEN.
   *
   * `servedArtifact` answers per language and only where the page was handed documents per language
   * — which is the product surface. The demo surface draws from the design and has none, so it
   * answers as it always did and nothing here changes for it. When the language on screen has no
   * document, the page says so and offers neither the download nor the share: showing the other
   * language's certificate under this language's name is the substitution this round exists to stop.
   */
  const servedByLocale = (c as { artifactSvgByLocale?: Record<string, unknown> }).artifactSvgByLocale;
  const documentMissing =
    servedByLocale !== undefined && (!sealedInLanguage(c, lang) || servedArtifact(c, lang) === null);
  const allowDl = perm.allowDownload && live && !documentMissing;
  /*
   * A dialog that was open when the reader switched to a language with no document does not stay
   * remembered. Guarding the render alone would unmount it and leave `dialog` set, so switching back
   * would reopen a window nobody asked for a second time. Cleared instead, so the state and what is
   * on screen say the same thing.
   */
  useEffect(() => {
    if (documentMissing && (dialog === 'share' || dialog === 'og')) setDialog(null);
  }, [documentMissing, dialog]);
  const cta: LinkedInCTA = resolveLinkedInCTA(c);
  const supBy = c.supersededByCode
    ? (db.credentials.find((x) => x.publicCode === c.supersededByCode) ?? null)
    : null;
  /* Subject transfer under one public code is forbidden, so a successor is only offered as «your»
     new record when it belongs to the same subject; otherwise the box states the fact and stops. */
  const sameHolder = Boolean(
    supBy &&
      (supBy.subjectSnapshot as { subjectId?: string }).subjectId ===
        (c.subjectSnapshot as { subjectId?: string }).subjectId,
  );

  const svg = useMemo(() => credentialArtwork(c, { lang }), [c, lang]);
  const exportSvg = useMemo(() => credentialArtwork(c, { lang, responsive: false }), [c, lang]);

  const openProfileAdd = (): void => {
    if (!cta.profileAdd) {
      toast('Bu kayıt için LinkedIn profil ekleme kullanılabilir değil.');
      return;
    }
    if (cta.profileAdd.flow === 'api_preconfirm') {
      setDialog('preconfirm');
      return;
    }
    if (cta.profileAdd.flow === 'api_authorize') {
      setDialog('authorize');
      return;
    }
    /* The form and the helper open in the SAME user gesture, so the browser treats the new tab as
       user-initiated; the helper still opens when it does not, with the re-open button. */
    /*
     * The LINK CARRIES THE CERTIFICATE. Name, issuing organisation, issue and expiry month/year,
     * the credential id and its verification URL all travel as query parameters, so the form the
     * recipient lands on is already filled in and they only check it and press Save.
     *
     * A record that cannot produce a complete link opens nothing and says so: an empty form would
     * look like success while leaving every field to be typed by hand.
     */
    const link = addToProfileLinkForCredential(c, credTitle(c, lang), c.release, settings);
    /*
     * A record that cannot fill the form still gets the helper — the per-field copy list is the
     * fallback and removing it would take away a working function. What it does NOT get is a tab
     * opened onto an empty LinkedIn form, which would look like the certificate had been sent.
     */
    if (!link.url) toast(L.liIncomplete);
    const w = link.url ? openNewTab(link.url) : null;
    /*
     * Measured AFTER the window is opened and never awaited: the tab a member asked for must not
     * wait on a request of ours, and a browser blocks a popup that opens a turn later. The row
     * says «ekleme başlatıldı» and nothing more — LinkedIn tells us nothing about what follows.
     */
    if (w) void commands.recordEngagement({ code: c.publicCode, kind: 'linkedin_profile_add_started' });
    copy(credTitle(c, lang), '');
    setPopupBlocked(!w);
    setDialog('helper');
  };

  return (
    <>
      {published ? (
        <div className="ok-box" style={{ marginBottom: '14px', fontSize: '14px' }}>
          {`✓ ${L.published}`}
        </div>
      ) : null}
      <article className={`cred-card ${s}`}>
        {documentMissing ? (
          <div className="cred-visual" id="noLocaleDocument">
            <p className="status-note" style={{ padding: '18px', margin: 0, textAlign: 'center' }}>
              {L.noDocumentInLanguage}
            </p>
          </div>
        ) : null}
        {/* The mosaic and the artwork are siblings inside the panel, as upstream: a wrapper around
            the SVG would take the `.cred-visual svg` width cap off the drawing. */}
        {/*
         * NOT RENDERED when this language has no document, rather than rendered and hidden.
         *
         * `hidden` kept the other language's drawing in the page — invisible, but in the source of a
         * page that says «this language has no version». The notice above stands in its place, so
         * what the page contains and what it says are the same thing.
         */}
        {documentMissing ? null : (
          <div
            className={`cred-visual ${credVisualClass(c)}`}
            dangerouslySetInnerHTML={{
              __html: `<div class="mosaic mosaic-corner" style="--cols:6">${mosaicTiles(6, 3, undefined, 9)
                .map((background) => `<i style="background:${background}"></i>`)
                .join('')}</div>${svg}`,
            }}
          />
        )}
        <section className="cred-info">
          <div>
            {s === 'active' ? (
              <>
                <div className="status active">
                  <span className="ico">✓</span>
                  {T.verified}
                </div>
                <p className="status-note">{L.noteActive(T)}</p>
              </>
            ) : s === 'expired' ? (
              <>
                <div className="status expired">
                  <span className="ico">○</span>
                  {T.expired}
                </div>
                <p className="status-note">{L.noteExpired(fmtDate(c.expiresAt, lang))}</p>
              </>
            ) : (
              <>
                <div className="status revoked">
                  <span className="ico">✕</span>
                  {T.revoked}
                </div>
                <p className="status-note">{L.noteRevoked(fmtDate(c.revokedAt, lang))}</p>
              </>
            )}
          </div>
          <div className="cred-title">
            <h1>{credTitle(c, lang) || L.noDocumentInLanguage}</h1>
            <p className="awarded">
              {isOrg ? L.holderOrg : L.holder}
              <b>{displayName(c)}</b>
            </p>
          </div>
          <dl className="cred-dl">
            <div>
              <dt>{L.issuedBy}</dt>
              <dd>{issuerName}</dd>
            </div>
            <div>
              <dt>{L.issueDate}</dt>
              <dd>{fmtDate(c.issueDate, lang)}</dd>
            </div>
            <div>
              <dt>{L.expiration}</dt>
              <dd>{c.expiresAt ? fmtDate(c.expiresAt, lang) : L.noExpiry}</dd>
            </div>
            {credPublicFields(c, lang).map((f) => (
              <div key={f.key}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
            {s === 'revoked' ? (
              <div>
                <dt>{L.revokedAt}</dt>
                <dd>{fmtDate(c.revokedAt, lang)}</dd>
              </div>
            ) : (
              <div>
                <dt>{L.updated}</dt>
                <dd>{fmtDate(c.updatedAt, lang)}</dd>
              </div>
            )}
            <div className="full">
              <dt>{L.credId}</dt>
              <dd className="code">
                <span>{c.publicCode}</span>
                <button
                  className="icon-btn"
                  type="button"
                  id="pubCopyCode"
                  title={L.copy}
                  aria-label={`${L.credId} ${L.copy}`}
                  onClick={() => copy(c.publicCode, L.copied)}
                >
                  <IconCopy />
                </button>
              </dd>
            </div>
            <div className="full">
              <dt>{L.credUrl}</dt>
              <dd className="code">
                <span style={{ wordBreak: 'break-all' }}>{publicUrl(c)}</span>
                <button
                  className="icon-btn"
                  type="button"
                  id="pubCopyUrl2"
                  title={L.copy}
                  aria-label={`${L.credUrl} ${L.copy}`}
                  onClick={() => {
                    /* Copied — and where it then went is not measured, which the history states. */
                    interaction.note('link_copied');
                    copy(publicUrl(c), L.linkCopied);
                  }}
                >
                  <IconCopy />
                </button>
              </dd>
            </div>
          </dl>
          {(s === 'revoked' || s === 'superseded') && supBy ? (
            <div className="info-box">
              {L.superseded}
              {sameHolder ? (
                <>
                  {' '}
                  <PublicLink to={`/c/${supBy.publicCode}`}>{`${L.supersededSame} →`}</PublicLink>
                </>
              ) : null}
            </div>
          ) : null}
          {s === 'revoked' || s === 'superseded' ? (
            <>
              <div className="cred-actions">
                <button
                  className="btn btn-ghost"
                  type="button"
                  id="pubCopyUrl"
                  onClick={() => {
                    /* Copied — and where it then went is not measured, which the history states. */
                    interaction.note('link_copied');
                    copy(publicUrl(c), L.linkCopied);
                  }}
                >
                  {L.copyUrl}
                </button>
              </div>
              <p className="small muted">{L.revokedHint}</p>
            </>
          ) : (
            <>
              <div className="cred-actions-grid">
                {cta.profileAdd ? (
                  <div className="action">
                    <button
                      className="btn li-btn btn-lg"
                      type="button"
                      id="pubLinkedIn"
                      data-flow={cta.profileAdd.flow}
                      onClick={openProfileAdd}
                    >
                      <span className="in">in</span>
                      {lang === 'en' ? L.addProfile : cta.profileAdd.label}
                    </button>
                    <span className="help">{lang === 'en' ? L.addProfileNote : cta.profileAdd.note}</span>
                    {cta.simulationBadge ? <span className="sim-inline">PROTOTİP SİMÜLASYONU</span> : null}
                  </div>
                ) : null}
                {/*
                 * NOT DRAWN when the language on screen has no document.
                 *
                 * What a share posts is this record's certificate, and there is no certificate in
                 * this language — so the button had nothing to offer but the other language's
                 * picture, which is precisely the substitution the locale rules forbid. The download
                 * has been closed by `documentMissing` since it was introduced and the comment above
                 * has said «neither the download nor the share» all along; the share half was the
                 * part that was never wired, and the reader could still open the dialog and see a
                 * preview of a document this record does not have.
                 *
                 * LinkedIn PROFILE ADD is deliberately untouched: it carries the record's name, id
                 * and verification URL as form fields and attaches no picture, so a language without
                 * a drawing does not make it dishonest.
                 */}
                {cta.feedShare && !documentMissing ? (
                  <div className="action">
                    <button
                      className="btn btn-dark btn-lg"
                      type="button"
                      id="pubShare"
                      /*
                       * Opens the HPW dialog and NOTHING else. It used to call `openNewTab` first,
                       * so the first click threw the reader into a LinkedIn tab before they had
                       * seen the picture or the text they were about to post — and the dialog they
                       * did want opened behind them. LinkedIn is now reached only from the control
                       * inside the dialog, after the reader has looked at what they are sharing.
                       */
                      onClick={() => setDialog('share')}
                    >
                      <IconSend />
                      {L.share}
                    </button>
                    <span className="help">{lang === 'en' ? L.shareNote : cta.feedShare.note}</span>
                  </div>
                ) : null}
              </div>
              <div className="cred-actions">
                {allowDl ? (
                  <>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      id="pubDownload"
                      onClick={() => {
                        /* A download was REQUESTED. Whether a file reached the device is not
                           something this page can see, and the history says exactly that. */
                        interaction.note('downloaded');
                        credentialPngDataUrl(exportSvg, 1)
                          .then((data) => {
                            if (downloadDataUrl(data, `${c.publicCode}.png`)) toast('PNG hazırlandı');
                          })
                          .catch((e: Error) => toast(`PNG üretilemedi: ${e.message}`));
                      }}
                    >
                      {T.download}
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      id="pubPrint"
                      onClick={() => {
                        /* The print WINDOW opening. «Yazdırıldı» is a claim nobody here can make. */
                        interaction.note('printed');
                        printCredential(c, exportSvg)
                          .then((ok) => {
                            if (!ok) toast('Yazdırma penceresi engellendi');
                          })
                          .catch(() => toast('Yazdırma görseli üretilemedi'));
                      }}
                    >
                      <IconPrint />
                      {L.print}
                    </button>
                  </>
                ) : null}
                <button
                  className="btn btn-ghost"
                  type="button"
                  id="pubCopyUrl"
                  onClick={() => {
                    /* Copied — and where it then went is not measured, which the history states. */
                    interaction.note('link_copied');
                    copy(publicUrl(c), L.linkCopied);
                  }}
                >
                  {L.copyUrl}
                </button>
                {/* The preview draws THIS language's document, so a language without one has no
                    preview to offer — and a button whose dialog cannot open is worse than none. */}
                {documentMissing ? null : (
                  <button
                    className="btn btn-ghost"
                    type="button"
                    id="pubOg"
                    onClick={() => {
                      interaction.note('og_preview_opened');
                      setDialog('og');
                    }}
                  >
                    {L.ogPreview}
                  </button>
                )}
              </div>
              {s === 'expired' ? <p className="small muted">{L.expiredHint}</p> : null}
            </>
          )}
          {/*
           * WHAT THIS RECORD IS — after the things a reader can DO with it.
           *
           * The prototype put the explanation between the certificate and its buttons, so on a
           * phone the reader met two headings and three paragraphs before reaching «LinkedIn'de
           * paylaş». The approved order is: the certificate's own facts, then the action group,
           * then the long text. One order for both widths — this is the document order, and the
           * stylesheet does not reflow it per breakpoint.
           */}
          {desc || crit ? (
            <div className="cred-about">
              {desc ? (
                <>
                  <h2>{T.about}</h2>
                  <p>{desc}</p>
                </>
              ) : null}
              {crit ? (
                <>
                  <h2>{L.criteria}</h2>
                  <p>{crit}</p>
                </>
              ) : null}
              <p className="small muted">{L.disclaimer}</p>
            </div>
          ) : null}
          <div className="verify-note">
            <b>{L.whatVerifies}</b>
            {` ${stmt} `}
            <span className="issuer-links">
              {`${L.issuerLinks}: `}
              <a href={issuerSite} target="_blank" rel="noopener">
                {L.website}
              </a>
              {' · '}
              <a href={issuerLi} target="_blank" rel="noopener">
                {L.liPage}
              </a>
            </span>
          </div>
        </section>
      </article>
      {dialog === 'helper' ? (
        <LinkedInHelperModal
          credential={c}
          lang={lang}
          settings={settings}
          popupBlocked={popupBlocked}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'share' && !documentMissing ? (
        <ShareModal
          credential={c}
          lang={lang}
          svg={exportSvg}
          popupBlocked={popupBlocked}
          allowDownload={Boolean(allowDl)}
          onOpenLinkedIn={() => {
            const w = openNewTab(linkedInShareUrl(publicUrl(c)));
            /*
             * «Paylaşım başlatıldı», never «Paylaşıldı»: this is a redirect to LinkedIn's own share
             * page, and whether anything was posted there is something only LinkedIn knows.
             */
            if (w) void commands.recordEngagement({ code: c.publicCode, kind: 'linkedin_share_started' });
            setPopupBlocked(!w);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'og' && !documentMissing ? (
        <OgPreviewModal credential={c} svg={exportSvg} lang={lang} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'authorize' ? (
        <LinkedInAuthorizeModal
          lang={lang}
          credential={c}
          settings={settings}
          onGranted={() => setDialog('preconfirm')}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'preconfirm' ? (
        <LinkedInApiPreconfirmModal
          credential={c}
          settings={settings}
          lang={lang}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** The waiting card: a lock, a status line and two facts. No name, no claims, no artwork. */
function PendingCard({
  credential: c,
  lang,
}: {
  credential: DemoCredential;
  lang: PublicLanguage;
}): ReactElement {
  const L = PUBLIC_I18N[lang];
  const T = credTerms(c, lang);
  const admin = credentialViewState(c) === 'pending_admin_publish';
  return (
    <article className="cred-card pending-card">
      <div className="cred-visual" style={{ minHeight: '260px' }}>
        <div className="pending-visual">
          <span className="lock">◔</span>
          <span>{L.pendingTitle}</span>
        </div>
      </div>
      <section className="cred-info">
        <div className="cred-title">
          <h1>{L.pendingTitle}</h1>
        </div>
        <div>
          <div className="status pending">
            <span className="ico">◔</span>
            {admin ? T.pendingAdmin : T.pending}
          </div>
          <p className="status-note">
            <b>{admin ? L.notePendingAdmin : L.notePending}</b>
          </p>
        </div>
        <p className="muted">{admin ? L.pendingBodyAdmin : L.pendingBody}</p>
        <dl className="cred-dl">
          <div>
            <dt>{L.issuedBy}</dt>
            <dd>{c.release.issuerProfileSnapshot.displayName}</dd>
          </div>
          <div className="full">
            <dt>{L.credId}</dt>
            <dd className="code">
              <span>{c.publicCode}</span>
            </dd>
          </div>
        </dl>
      </section>
    </article>
  );
}

// ------------------------------------------------------------------------------------ the screen

/** The `#/c/:code` surface. Mounted by the demo router, which passes nothing. */
export function PublicCredentialScreen(): ReactElement {
  const db = useDemoData();
  const settings = useSettings();
  const params = useParams();
  const { pathname } = useLocation();
  const [search, setSearch] = useSearchParams();
  /* What the ADDRESS asked for wins over what this browser remembers: a link that says English is a
     link to the English document, whoever opens it. */
  const asked = askedLanguage(search.get('lang'));
  const [lang, setLangState] = useState<PublicLanguage>(() =>
    asked.kind === 'ok' ? asked.lang : storedLang(),
  );

  /* The demo router owns the route table. Reading the path as well means a `/c/*` mounting and a
     `/c/:code` one both resolve to the same record instead of silently answering «404». */
  const fromPath = /\/c\/([^/?#]+)/.exec(pathname);
  const code = params.code ?? (fromPath?.[1] ? decodeURIComponent(fromPath[1]) : '');
  const L = PUBLIC_I18N[lang];

  const setLang = (next: PublicLanguage): void => {
    setLangState(next);
    /* Written into the address so the page can be handed on in the language it is being read in.
       `replace` — switching language is not a step in the visitor's history. */
    const params = new URLSearchParams(search);
    params.set('lang', next);
    setSearch(params, { replace: true });
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* A blocked storage must not take the language switch down with it. */
    }
  };

  const look = lookupByPublicCode(code, db.credentials);

  let body: ReactElement;
  if (!look.ok && look.code === 'public_code_ambiguous_collision') {
    body = (
      <div className="notfound">
        <div className="big">409</div>
        <h1>{L.ambiguousTitle}</h1>
        <p className="muted">{L.ambiguousNote}</p>
        <p className="small muted mono">{L.ambiguousCode}</p>
        <LookupForm placeholder={L.lookupPh} submit={L.lookup} initial={code} />
      </div>
    );
  } else if (!look.ok) {
    body = (
      <div className="notfound">
        <div className="big">404</div>
        <h1>{L.notFound}</h1>
        <p className="muted">{L.notFoundNote}</p>
        <LookupForm placeholder={L.lookupPh} submit={L.lookup} initial={code} />
      </div>
    );
  } else {
    const c = look.credential;
    const s = credentialViewState(c);
    body =
      s === 'pending_acceptance' || s === 'pending_admin_publish' || s === 'draft' ? (
        <PendingCard credential={c} lang={lang} />
      ) : (
        <CredentialCard
          credential={c}
          lang={lang}
          db={db}
          settings={settings}
          published={Boolean(search.get('published'))}
        />
      );
  }

  return (
    <PublicShell id="publicScreen" brandSub={L.brandSub} lang={lang} onLang={setLang} footer={L.foot}>
      {/*
       * The address asked for a language this page does not have.
       *
       * Said rather than swallowed: the visitor asked for something and got something else, and the
       * page names which language it IS showing. It is not treated as «Turkish, near enough» — the
       * artifact route refuses the same value outright (`404 unsupported_locale`).
       */}
      {asked.kind === 'unsupported' ? (
        <div className="warn-box small" id="unsupportedLang" style={{ marginBottom: '14px' }}>
          {L.unsupportedLang(asked.value, lang === 'en' ? 'English' : 'Türkçe')}
        </div>
      ) : null}
      {body}
    </PublicShell>
  );
}

/** `#/verify` — the same shell, the same lookup form, no record. */
export function PublicVerifyScreen(): ReactElement {
  const [lang, setLangState] = useState<PublicLanguage>(storedLang);
  const L = PUBLIC_I18N[lang];
  const setLang = (next: PublicLanguage): void => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* see above */
    }
  };
  return (
    <PublicShell id="publicScreen" brandSub={L.brandSub} lang={lang} onLang={setLang} footer={L.foot}>
      <div className="notfound" style={{ marginTop: '60px' }}>
        <span className="eyebrow">Happy Place to Work</span>
        <h1>{L.verifyTitle}</h1>
        <p className="muted">{L.verifyIntro}</p>
        <LookupForm placeholder={L.lookupPh} submit={L.lookup} />
      </div>
    </PublicShell>
  );
}
